"use client";
import * as React from "react";
import { useRouter, usePathname } from "next/navigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster, toast } from "sonner";
import { ThemeProvider } from "next-themes";

import { setUnauthorizedHandler } from "@/lib/api-client";
import { clearSessionClient } from "@/lib/session";
import { forceDisconnectSocket } from "@/lib/socket";
import { ConfirmHost } from "@/lib/confirm";
import { VisibleErrorBoundary } from "@/components/error-boundary";

export function Providers({ children }: { children: React.ReactNode }) {
  const [qc] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 默认 30 秒内不重拉，配合行级 patch 更省流量
            staleTime: 30_000,
            retry: (failureCount, err: any) => {
              // 401 不重试，直接抛
              if (err?.status === 401) return false;
              return failureCount < 2;
            },
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: 0,
          },
        },
      })
  );

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider client={qc}>
        <ChunkErrorReloadGate />
        <VisibleErrorBoundary>
          <AuthGate>{children}</AuthGate>
        </VisibleErrorBoundary>
        <Toaster position="top-right" richColors closeButton theme="system" />
        <ConfirmHost />
        {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * Chunk 加载失败兜底：
 * 部署后用户浏览器持有旧 HTML（引用旧 hash 的 chunk），新部署清掉旧 chunk →
 * 用户点导出（动态 import exceljs）→ 拉旧 chunk 404 → 报 "Loading chunk X failed"。
 * Nginx 已加 no-store 让 HTML 永远拉最新，但用户当前打开的页面还是旧 HTML，
 * 第一次触发时仍会失败 → 这里捕获后自动 reload 一次拿新 HTML。
 *
 * 防死循环：sessionStorage 记一次 reload 时间戳，5 分钟内不重复 reload，
 * 改成 toast 提示用户手动刷新（避免环境真有问题反复抖）。
 */
const CHUNK_RELOAD_KEY = "xyt_chunk_reload_at";
const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

function isChunkError(e: unknown): boolean {
  const msg = String((e as { message?: string })?.message || e || "");
  return /Loading chunk \S+ failed|Loading CSS chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(msg);
}

function ChunkErrorReloadGate() {
  React.useEffect(() => {
    // sessionStorage 在 Firefox 隐私 / Safari ITP / 某些扩展下可能抛 SecurityError，
    // 必须用 try/catch 包，否则 ChunkErrorReloadGate 自己就会再抛 → ErrorBoundary
    // 兜底显示通用 "Application error"。
    function safeGet(k: string): string | null {
      try { return sessionStorage.getItem(k); } catch { return null; }
    }
    function safeSet(k: string, v: string) {
      try { sessionStorage.setItem(k, v); } catch { /* quota / private mode */ }
    }
    function handle(e: unknown, source: string) {
      if (!isChunkError(e)) return;
      const last = Number(safeGet(CHUNK_RELOAD_KEY) || 0);
      if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) {
        toast.error("加载资源失败，请手动刷新页面（F5）后重试", {
          duration: 8000,
          description: `来源：${source}`,
        });
        return;
      }
      safeSet(CHUNK_RELOAD_KEY, String(Date.now()));
      toast("检测到部署更新，正在自动刷新页面…", { duration: 2000 });
      setTimeout(() => location.reload(), 600);
    }
    // 浏览器扩展（FloatingAssistant / Immersive Translate / Chrome AI 等）
    // 注入的脚本经常报错 —— 它们的 styled-components / hydration 失败和我们
    // 完全无关，但会冒泡到 window.error 把 console 刷到几百行、还触发 React
    // DevTools 的额外开销 → 体感卡顿。在监听器入口直接吞掉来自扩展源的错误。
    function isFromExtension(ev: ErrorEvent | { filename?: string; reason?: unknown; error?: unknown; message?: string }): boolean {
      const fn = (ev as ErrorEvent).filename || "";
      if (/^(chrome|moz|safari-web|webkit)-extension:\/\//i.test(fn)) return true;
      if (/FloatingAssistant|content-script\.[a-f0-9]+\.js|content_main\.js|installHook\.js/i.test(fn)) return true;
      const reason = (ev as { reason?: unknown }).reason;
      const err = (ev as { error?: unknown }).error;
      const msg = String(
        (reason as { message?: string })?.message ||
        (err as { message?: string })?.message ||
        (ev as { message?: string }).message ||
        reason || err || ""
      );
      if (/styled-components|FloatingAssistant|Immersive Translate/i.test(msg)) return true;
      // React 19 hydration 错误 #310 / #418 / #423 / #425 已经被 React 内部
      // 自动恢复到 client render，但错误对象仍冒泡到 window.error。
      // 这类错误几乎都是浏览器扩展往 body / 子树里塞 DOM 触发的，吞掉
      // 不影响功能，避免刷屏 + 不会让用户看到 "Application error"。
      // #310 是 #418 的级联（hydration mismatch 后 React client re-render，
      // 扩展 DOM 让条件分支变化 → hook 数变 → #310），跟 #418 一并吞。
      if (/Minified React error #(310|418|423|425|421|422)/.test(msg)) return true;
      return false;
    }
    const onError = (ev: ErrorEvent) => {
      if (isFromExtension(ev)) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
        return;
      }
      handle(ev.error || ev.message, "error");
    };
    const onReject = (ev: PromiseRejectionEvent) => {
      if (isFromExtension(ev as unknown as ErrorEvent)) {
        ev.preventDefault();
        return;
      }
      handle(ev.reason, "rejection");
    };
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onReject);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onReject);
    };
  }, []);
  return null;
}

/**
 * 401 全局拦截器：任何 API 返回 401 → 清 cookie + toast + 跳 /login?next=<当前>
 * 同一次会话只触发一次（setUnauthorizedHandler 内部已有 flag）
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  React.useEffect(() => {
    setUnauthorizedHandler((err) => {
      // 在登录页触发的 401 不要循环跳
      if (pathname.startsWith("/login")) return;
      // 主动断 socket，阻断 Realtime → invalidate → refetch → 又 401 的循环
      forceDisconnectSocket();
      clearSessionClient();
      toast.error(err.message || "会话已过期，请重新登录", { duration: 5000 });
      const next = encodeURIComponent(pathname || "/");
      router.push(`/login?next=${next}`);
      setTimeout(() => router.refresh(), 100);
    });
    return () => setUnauthorizedHandler(null);
  }, [pathname, router]);

  return <>{children}</>;
}
